// ⌘K: jump to a project, episode, scene, shot, element, canvas or folder, or run a command. Type to filter,
// arrows move, Enter goes, Escape closes.
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { SUBTYPE_LABEL } from "../elements.ts";
import type { Folder } from "../folders.ts";
import { episodeCode, episodeName, firstWords, type Glyph, handleNames, sizeName } from "../items.ts";
import type { Route } from "../route.ts";
import type { ElementSubtype, Project } from "../types.ts";
import type { ProjectData } from "../useProject.ts";
import { useApp } from "./app.ts";
import { useCollabCommands } from "./collab/Keys.tsx";
import { containDialogFocus } from "./dialogFocus.ts";
import { Icon, type IconName } from "./icons.tsx";

export interface PaletteEntry {
  id: string;
  group: string;
  title: string;
  code?: string;
  hint?: string;
  /** A keyboard shortcut chip; not shown on touch or narrow screens. */
  keys?: string;
  icon: IconName;
  route?: Route;
  run?: () => void;
}

/** Subsequence match with a bonus for word starts and contiguous runs; 0 = no match. */
export function score(query: string, text: string): number {
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  const t = text.toLowerCase();
  const direct = t.indexOf(q);
  if (direct >= 0) return 1000 - direct + (direct === 0 || /\W/.test(t[direct - 1] ?? "") ? 200 : 0);
  let s = 0;
  let ti = 0;
  let run = 0;
  for (const ch of q) {
    if (ch === " ") continue;
    const at = t.indexOf(ch, ti);
    if (at < 0) return 0;
    run = at === ti ? run + 1 : 0;
    s += 1 + run * 2 + (at === 0 || /\W/.test(t[at - 1] ?? "") ? 3 : 0);
    ti = at + 1;
  }
  return s;
}

export function paletteEntries(
  projects: Project[],
  d: ProjectData | null,
  folders: Folder[],
): PaletteEntry[] {
  const out: PaletteEntry[] = projects.map((p) => ({
    id: `p:${p.id}`,
    group: "Productions",
    title: p.name,
    hint: p.type,
    icon: "folder",
    route: { view: "project", projectId: p.id, loc: { kind: "root" } },
  }));
  if (!d) return out;
  const pid = d.project.id;
  for (const e of d.episodes) {
    out.push({
      id: `e:${e.id}`,
      group: "Episodes",
      title: episodeName(e),
      code: episodeCode(e),
      icon: "episode",
      route: { view: "project", projectId: pid, loc: { kind: "episode", id: e.id } },
    });
    out.push({
      id: `cut:${e.id}`,
      group: "Episodes",
      title: `Cut of ${episodeName(e)}`,
      code: episodeCode(e),
      hint: "Watch, note, retake",
      icon: "episode",
      route: { view: "project", projectId: pid, loc: { kind: "cut", episodeId: e.id } },
    });
    for (const s of d.scenes.get(e.id) ?? [])
      out.push({
        id: `s:${s.id}`,
        group: "Scenes",
        title: s.slugline ?? `Scene ${s.code}`,
        code: s.code,
        hint: episodeName(e),
        icon: "scene",
        route: { view: "project", projectId: pid, loc: { kind: "scene", episodeId: e.id, id: s.id } },
      });
  }
  for (const [, list] of d.shots)
    for (const s of list)
      out.push({
        id: `sh:${s.id}`,
        group: "Shots",
        title: firstWords(s.description, 64, handleNames(d)) || sizeName(s.size),
        code: s.code,
        icon: "shot",
        route: { view: "shot", projectId: pid, shotId: s.id },
      });
  for (const el of d.elements)
    out.push({
      id: `el:${el.id}`,
      group: "Elements",
      title: el.name,
      hint: `@${el.handle} · ${SUBTYPE_LABEL[el.subtype as ElementSubtype]?.replace(/s$/, "") ?? el.subtype}`,
      icon: (el.subtype as Glyph) ?? "elements",
      route: { view: "element", projectId: pid, elementId: el.id },
    });
  for (const c of d.canvases)
    out.push({
      id: `c:${c.id}`,
      group: "Canvases",
      title: c.name ?? "Untitled canvas",
      icon: "canvas",
      route: { view: "canvas", projectId: pid, id: c.id },
    });
  for (const f of folders)
    out.push({
      id: `f:${f.id}`,
      group: "Folders",
      title: f.name,
      icon: "folder",
      route: { view: "project", projectId: pid, loc: { kind: "folder", id: f.id } },
    });
  return out;
}

/** One heading per group: groups in the order they first appear, entries keep their order inside them. */
export function byGroup(list: PaletteEntry[]): PaletteEntry[] {
  const groups = new Map<string, PaletteEntry[]>();
  for (const e of list) groups.set(e.group, [...(groups.get(e.group) ?? []), e]);
  return [...groups.values()].flat();
}

const dedupe = (list: PaletteEntry[]) => {
  const seen = new Set<string>();
  return list.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
};

export function Palette({
  entries,
  commands: baseCommands,
  onGo,
  onClose,
}: {
  entries: PaletteEntry[];
  commands: PaletteEntry[];
  onGo: (e: PaletteEntry) => void;
  onClose: () => void;
}) {
  const { compact } = useApp();
  const collab = useCollabCommands();
  const commands = useMemo(() => [...collab, ...baseCommands], [collab, baseCommands]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (dialog.current && input.current) return containDialogFocus(dialog.current, input.current);
  }, []);

  const results = useMemo(() => {
    const all = dedupe([...commands, ...entries]);
    if (!q.trim()) return byGroup([...commands, ...entries.filter((e) => e.group !== "Shots")].slice(0, 40));
    return byGroup(
      all
        .map((e) => ({
          e,
          s: Math.max(score(q, e.title), score(q, e.code ?? "") * 1.2, score(q, e.hint ?? "") * 0.5),
        }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 40)
        .map((x) => x.e),
    );
  }, [q, entries, commands]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: back to the top on every new query
  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") setActive((a) => Math.min(results.length - 1, a + 1));
    else if (e.key === "ArrowUp") setActive((a) => Math.max(0, a - 1));
    else if (e.key === "Enter" && results[active]) onGo(results[active]!);
    else if (e.key === "Escape") onClose();
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  let lastGroup = "";
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: clicking the backdrop closes; Escape does too
    <div className="cmdk-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Jump to"
        ref={dialog}
        onKeyDown={onKey}
      >
        <div className="cmdk-input">
          <Icon name="search" />
          <input
            ref={input}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={results[active] ? `pal-${active}` : undefined}
            aria-autocomplete="list"
            placeholder={compact ? "Jump to…" : "Jump to a shot, scene, element, canvas or folder…"}
            aria-label="Jump to"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {!compact && <kbd>esc</kbd>}
        </div>
        <div className="cmdk-list" id="palette-list" role="listbox" ref={list} aria-label="Results">
          {results.length === 0 && <p className="cmdk-empty">Nothing matches “{q}”.</p>}
          {results.map((r, i) => {
            const head = r.group !== lastGroup;
            lastGroup = r.group;
            return (
              <div key={r.id} role="presentation">
                {head && <div className="cmdk-group">{r.group}</div>}
                <div
                  id={`pal-${i}`}
                  data-i={i}
                  role="option"
                  tabIndex={-1}
                  aria-selected={i === active}
                  className={`cmdk-row${i === active ? " on" : ""}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => onGo(r)}
                  onKeyDown={() => {}}
                >
                  <Icon name={r.icon} />
                  {r.code && <span className="code">{r.code}</span>}
                  <span className="title">{r.title}</span>
                  {r.hint && <span className="hint">{r.hint}</span>}
                  {r.keys && <kbd className="row-keys">{r.keys}</kbd>}
                  {i === active && !compact && <kbd>↵</kbd>}
                </div>
              </div>
            );
          })}
        </div>
        {!compact && (
          <div className="cmdk-foot">
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> move
            </span>
            <span>
              <kbd>↵</kbd> open
            </span>
            <span>
              <kbd>esc</kbd> close
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
